import posthog from 'posthog-js'
import { ref } from 'vue'

const isInitialized = ref(false)

export interface PostHogConfig {
  apiKey?: string
  host?: string
}

/**
 * PostHog tracking composable for the widget
 * Tracks user interactions and events
 */
export function usePostHog() {
  /**
   * Initialize PostHog with API key
   */
  function init(config: PostHogConfig) {
    // Get API key from config or environment
    const apiKey = config.apiKey || import.meta.env.VITE_POSTHOG_API_KEY || 'phc_cQI851MypbY3FVI1JJkLuA4MFUVClnkHVhVRY7Snh5W'
    const host = config.host || import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'

    if (!apiKey) {
      console.warn('[PostHog] No API key provided, tracking disabled')
      return
    }

    // Check if running in production by checking the hostname
    // Widget should track when loaded from any production domain
    // TEMPORARY: Force enable for localhost testing
    const isProduction = true // Temporarily always true for testing
    // const isProduction = window.location.hostname !== 'localhost' &&
    //                      window.location.hostname !== '127.0.0.1' &&
    //                      !window.location.hostname.includes('.local')

    console.log('[PostHog] Environment check:', {
      hostname: window.location.hostname,
      isProduction,
      willTrack: isProduction
    })

    try {
      posthog.init(apiKey, {
        api_host: host,
        person_profiles: 'identified_only', // Only create profiles for identified users

        // Cross-origin/embedded widget configuration
        // Use sessionStorage which is less restrictive than localStorage for cross-origin
        persistence: 'sessionStorage',
        cross_subdomain_cookie: false, // Don't try to set cross-subdomain cookies

        // Session recording - disabled for embedded widgets
        disable_session_recording: true, // Fully disable session recording

        // Capture settings
        capture_pageview: false, // We'll track widget opens instead
        capture_pageleave: false, // Don't track page leave events
        autocapture: false, // Disable autocapture for embedded widgets

        // Advanced options
        advanced_disable_decide: true, // Skip feature flags and experiments to avoid extra requests

        // Disable in development/localhost only
        loaded: (ph) => {
          if (!isProduction) {
            ph.opt_out_capturing()
            console.log('[PostHog] Tracking disabled - running on localhost/development')
          } else {
            console.log('[PostHog] Tracking enabled - running in production environment')
          }
        }
      })

      isInitialized.value = true
      console.log('[PostHog] Initialized successfully', {
        isProduction,
        trackingEnabled: isProduction
      })
    } catch (error) {
      console.error('[PostHog] Initialization failed:', error)
    }
  }

  /**
   * Identify the user with properties
   */
  function identify(userId: string, properties: Record<string, any> = {}) {
    if (!isInitialized.value) return

    try {
      posthog.identify(userId, properties)
      console.log('[PostHog] User identified:', userId)
    } catch (error) {
      console.error('[PostHog] Failed to identify user:', error)
    }
  }

  /**
   * Track a custom event
   */
  function track(eventName: string, properties: Record<string, any> = {}) {
    if (!isInitialized.value) return

    try {
      posthog.capture(eventName, properties)
      console.log('[PostHog] Event tracked:', eventName, properties)
    } catch (error) {
      console.error('[PostHog] Failed to track event:', error)
    }
  }

  /**
   * Track widget opened
   */
  function trackWidgetOpened(properties: Record<string, any> = {}) {
    track('widget_opened', properties)
  }

  /**
   * Track widget closed
   */
  function trackWidgetClosed(properties: Record<string, any> = {}) {
    track('widget_closed', properties)
  }

  /**
   * Track tab switch
   */
  function trackTabSwitch(tabName: string, properties: Record<string, any> = {}) {
    track('widget_tab_switched', {
      tab: tabName,
      ...properties
    })
  }

  /**
   * Track message sent
   */
  function trackMessageSent(properties: Record<string, any> = {}) {
    track('chat_message_sent', properties)
  }

  /**
   * Track template used
   */
  function trackTemplateUsed(templateText: string, properties: Record<string, any> = {}) {
    track('chat_template_used', {
      template: templateText,
      ...properties
    })
  }

  /**
   * Track help dialog opened
   */
  function trackHelpOpened(properties: Record<string, any> = {}) {
    track('help_dialog_opened', properties)
  }

  /**
   * Track help dialog closed
   */
  function trackHelpClosed(properties: Record<string, any> = {}) {
    track('help_dialog_closed', properties)
  }

  /**
   * Track conversation reset
   */
  function trackConversationReset(properties: Record<string, any> = {}) {
    track('conversation_reset', properties)
  }

  /**
   * Set user properties
   */
  function setUserProperties(properties: Record<string, any>) {
    if (!isInitialized.value) return

    try {
      posthog.people.set(properties)
    } catch (error) {
      console.error('[PostHog] Failed to set user properties:', error)
    }
  }

  /**
   * Reset PostHog (logout)
   */
  function reset() {
    if (!isInitialized.value) return

    try {
      posthog.reset()
      console.log('[PostHog] Reset successfully')
    } catch (error) {
      console.error('[PostHog] Failed to reset:', error)
    }
  }

  return {
    isInitialized,
    init,
    identify,
    track,
    trackWidgetOpened,
    trackWidgetClosed,
    trackTabSwitch,
    trackMessageSent,
    trackTemplateUsed,
    trackHelpOpened,
    trackHelpClosed,
    trackConversationReset,
    setUserProperties,
    reset
  }
}
